"use client"

import { useState, useEffect, useRef } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { getToken } from "../utils/auth.ts"
import { useCompany } from "../context/CompanyContext.tsx"
import axiosInstance from "../utils/axiosConfig.ts"
import { Sidebar } from "../components/sideNavbar.tsx"
import { Topbar } from "../components/topNavbar.tsx"
import { useOptionalAdminChrome } from "../context/AdminLayoutContext.tsx"
import {
  AlertCircle,
  Calendar,
  CheckCircle,
  Clock,
  FileText,
  MessageSquare,
  Phone,
  Star,
  User,
  ArrowLeft,
  Paperclip,
  Tag,
  Edit,
  Save,
  X,
  Wifi,
  Zap,
} from "lucide-react"
import { toast } from "../utils/notify.ts"
import { smartoltService } from "../services/smartolt.service.ts"

interface Complaint {
  id: string
  customer_id: string
  customer_name: string
  internet_id: string
  phone_number: string
  description: string
  status: "open" | "in_progress" | "resolved" | "closed"
  assigned_to: string | null
  assigned_to_name: string | null
  created_at: string
  updated_at: string
  resolved_at: string | null
  response_due_date: string | null
  satisfaction_rating: number | null
  resolution_attempts: number
  attachment_path: string | null
  feedback_comments: string | null
  is_active: boolean
  resolution_proof: string | null
  ticket_number: string
  remarks: string | null
}

const ComplaintDetailPage = () => {
  const { id } = useParams<{ id: string }>()
  const [complaint, setComplaint] = useState<Complaint | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const hasChrome = useOptionalAdminChrome()
  const [isSidebarOpen, setIsSidebarOpen] = useState(false)
  const [isEditingRemarks, setIsEditingRemarks] = useState(false)
  const [remarks, setRemarks] = useState<string>("")
  const [isSavingRemarks, setIsSavingRemarks] = useState(false)
  const [smartoltDiagnostics, setSmartoltDiagnostics] = useState<any>(null)
  const [smartoltLoading, setSmartoltLoading] = useState(false)
  const [smartoltActionBusy, setSmartoltActionBusy] = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const navigate = useNavigate()

  const toggleSidebar = () => {
    setIsSidebarOpen((prev) => !prev)
  }

  const { setPageTitle } = useCompany()

  useEffect(() => {
    setPageTitle("Complaint Details")
    const fetchComplaintData = async () => {
      try {
        setLoading(true)
        const token = getToken()
        const response = await axiosInstance.get(`/complaints/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        setComplaint(response.data)
        setRemarks(response.data.remarks || "")
        setLoading(false)
      } catch (error) {
        console.error("Failed to fetch complaint data", error)
        setError("Failed to load complaint details. Please try again later.")
        setLoading(false)
      }
    }

    if (id) {
      fetchComplaintData()
    }
  }, [id])

  useEffect(() => {
    // Focus the textarea when editing mode is enabled
    if (isEditingRemarks && textareaRef.current) {
      textareaRef.current.focus()
    }
  }, [isEditingRemarks])

  useEffect(() => {
    const loadDiagnostics = async () => {
      if (!id) return
      setSmartoltLoading(true)
      try {
        const data = await smartoltService.getComplaintDiagnostics(id)
        setSmartoltDiagnostics(data)
      } catch {
        setSmartoltDiagnostics(null)
      } finally {
        setSmartoltLoading(false)
      }
    }
    loadDiagnostics()
  }, [id])

  const handleDownloadAttachment = () => {
    if (complaint?.attachment_path) {
      const token = getToken()
      fetch(`/complaints/attachment/${id}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
        .then((response) => response.blob())
        .then((blob) => {
          const url = window.URL.createObjectURL(blob)
          const a = document.createElement("a")
          a.style.display = "none"
          a.href = url
          a.download = `complaint_attachment_${complaint.ticket_number}`
          document.body.appendChild(a)
          a.click()
          window.URL.revokeObjectURL(url)
        })
        .catch((error) => console.error("Error:", error))
    }
  }

  const handleEditRemarks = () => {
    setIsEditingRemarks(true)
  }

  const handleCancelEditRemarks = () => {
    setRemarks(complaint?.remarks || "")
    setIsEditingRemarks(false)
  }

  const handleSaveRemarks = async () => {
    if (!id) return

    try {
      setIsSavingRemarks(true)
      const token = getToken()
      await axiosInstance.put(
        `/complaints/update-remarks/${id}`,
        { remarks },
        { headers: { Authorization: `Bearer ${token}` } },
      )

      // Update the local complaint object with the new remarks
      if (complaint) {
        setComplaint({
          ...complaint,
          remarks,
        })
      }

      setIsEditingRemarks(false)
      toast.success("Remarks updated successfully")
    } catch (error) {
      console.error("Failed to update remarks", error)
      toast.error("Failed to update remarks")
    } finally {
      setIsSavingRemarks(false)
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case "open":
        return "bg-yellow-100 text-yellow-800"
      case "in_progress":
        return "bg-blue-100 text-blue-800"
      case "resolved":
        return "bg-green-100 text-green-800"
      case "closed":
        return "bg-gray-100 text-gray-800"
      default:
        return "bg-gray-100 text-gray-800"
    }
  }

  const runSmartOLTAction = async (operationKey: string) => {
    if (!id) return
    if (operationKey.includes("disable")) {
      const confirm = window.prompt("Type DISABLE to confirm this action")
      if (confirm !== "DISABLE") return
    }
    setSmartoltActionBusy(true)
    try {
      await smartoltService.submitComplaintAction(id, operationKey, {})
      await smartoltService.runCommandsNow(5)
      toast.success("SmartOLT command queued")
      const refreshed = await smartoltService.getComplaintDiagnostics(id)
      setSmartoltDiagnostics(refreshed)
    } catch (error: any) {
      toast.error(error?.response?.data?.error || "Failed to queue SmartOLT action")
    } finally {
      setSmartoltActionBusy(false)
    }
  }

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-100">
        <div className="flex flex-col items-center space-y-4">
          <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-t-2 border-[#8b5cf6]"></div>
          <p className="text-lg font-medium text-[#8b5cf6]">Loading complaint details...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-100">
        <div className="max-w-md rounded-lg bg-white p-8 shadow-lg">
          <div className="mb-4 flex justify-center">
            <AlertCircle className="h-16 w-16 text-red-500" />
          </div>
          <h2 className="mb-4 text-center text-2xl font-bold text-gray-800">Error</h2>
          <p className="text-center text-gray-600">{error}</p>
          <div className="mt-6 flex justify-center">
            <button
              onClick={() => navigate("/complaints")}
              className="rounded-md bg-[#8b5cf6] px-4 py-2 text-white transition duration-300 hover:bg-[#7c3aed]"
            >
              Back to Complaints
            </button>
          </div>
        </div>
      </div>
    )
  }

  if (!complaint) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-100">
        <div className="max-w-md rounded-lg bg-white p-8 shadow-lg">
          <div className="mb-4 flex justify-center">
            <AlertCircle className="h-16 w-16 text-yellow-500" />
          </div>
          <h2 className="mb-4 text-center text-2xl font-bold text-gray-800">Complaint Not Found</h2>
          <p className="text-center text-gray-600">
            The complaint you're looking for doesn't exist or you don't have permission to view it.
          </p>
          <div className="mt-6 flex justify-center">
            <button
              onClick={() => navigate("/complaints")}
              className="rounded-md bg-[#8b5cf6] px-4 py-2 text-white transition duration-300 hover:bg-[#7c3aed]"
            >
              Back to Complaints
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={hasChrome ? "flex-1 min-w-0 w-full" : "flex h-screen bg-gray-100"}>
      {!hasChrome && (
        <Sidebar isOpen={isSidebarOpen} toggleSidebar={toggleSidebar} setIsOpen={setIsSidebarOpen} />
      )}
      <div className={hasChrome ? "flex-1 min-w-0 w-full" : "flex-1 flex flex-col overflow-hidden"}>
        {!hasChrome && <Topbar toggleSidebar={toggleSidebar} />}
        <main
          className={
            hasChrome
              ? "px-6 py-6"
              : `flex-1 overflow-x-hidden overflow-y-auto bg-gray-100 px-6 pb-6 pt-20 transition-all duration-300 ${
            isSidebarOpen ? "lg:ml-72" : "ml-0 lg:ml-20"
          }`
          }
        >
          <div className="container mx-auto max-w-5xl">
            {/* Header with back button */}
            <div className="mb-6 flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <button
                  onClick={() => navigate("/complaints")}
                  className="flex items-center rounded-full bg-white p-2 text-gray-600 shadow-sm transition-colors hover:bg-gray-50 hover:text-[#8b5cf6]"
                >
                  <ArrowLeft className="h-5 w-5" />
                </button>
                <h1 className="text-3xl font-bold text-[#8b5cf6]">Complaint Details</h1>
              </div>
            </div>

            {/* Ticket info card */}
            <div className="mb-6 overflow-hidden rounded-xl bg-white shadow-md">
              <div className="bg-gradient-to-r from-[#8b5cf6] to-[#9f7aea] p-4">
                <div className="flex flex-wrap items-center justify-between">
                  <div className="mb-2 flex items-center space-x-3 md:mb-0">
                    <Tag className="h-6 w-6 text-white" />
                    <h2 className="text-xl font-semibold text-white">Ticket #{complaint.ticket_number}</h2>
                  </div>
                  <div>
                    <span
                      className={`inline-flex items-center rounded-full px-3 py-1 text-sm font-medium ${getStatusColor(
                        complaint.status,
                      )}`}
                    >
                      {complaint.status.charAt(0).toUpperCase() + complaint.status.slice(1)}
                    </span>
                  </div>
                </div>
              </div>

              <div className="grid gap-6 p-6 md:grid-cols-2">
                <div className="space-y-6">
                  <div>
                    <h3 className="mb-2 text-sm font-medium text-gray-500">Customer Information</h3>
                    <div className="rounded-lg bg-gray-50 p-4">
                      <div className="mb-3 flex items-start space-x-3">
                        <User className="mt-1 h-5 w-5 flex-shrink-0 text-[#8b5cf6]" />
                        <div>
                          <p className="text-sm font-medium text-gray-500">Name</p>
                          <p className="text-gray-800">{complaint.customer_name}</p>
                        </div>
                      </div>
                      <div className="mb-3 flex items-start space-x-3">
                        <Tag className="mt-1 h-5 w-5 flex-shrink-0 text-[#8b5cf6]" />
                        <div>
                          <p className="text-sm font-medium text-gray-500">Internet ID</p>
                          <p className="text-gray-800">{complaint.internet_id}</p>
                        </div>
                      </div>
                      <div className="flex items-start space-x-3">
                        <Phone className="mt-1 h-5 w-5 flex-shrink-0 text-[#8b5cf6]" />
                        <div>
                          <p className="text-sm font-medium text-gray-500">Phone Number</p>
                          <p className="text-gray-800">{complaint.phone_number}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="mb-2 text-sm font-medium text-gray-500">Assignment Information</h3>
                    <div className="rounded-lg bg-gray-50 p-4">
                      <div className="mb-3 flex items-start space-x-3">
                        <User className="mt-1 h-5 w-5 flex-shrink-0 text-[#8b5cf6]" />
                        <div>
                          <p className="text-sm font-medium text-gray-500">Assigned To</p>
                          <p className="text-gray-800">{complaint.assigned_to_name || "Unassigned"}</p>
                        </div>
                      </div>
                      <div className="flex items-start space-x-3">
                        <Clock className="mt-1 h-5 w-5 flex-shrink-0 text-[#8b5cf6]" />
                        <div>
                          <p className="text-sm font-medium text-gray-500">Resolution Attempts</p>
                          <p className="text-gray-800">{complaint.resolution_attempts}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-6">
                  <div>
                    <h3 className="mb-2 text-sm font-medium text-gray-500">Dates</h3>
                    <div className="rounded-lg bg-gray-50 p-4">
                      <div className="mb-3 flex items-start space-x-3">
                        <Calendar className="mt-1 h-5 w-5 flex-shrink-0 text-[#8b5cf6]" />
                        <div>
                          <p className="text-sm font-medium text-gray-500">Created At</p>
                          <p className="text-gray-800">{new Date(complaint.created_at).toLocaleString()}</p>
                        </div>
                      </div>
                      <div className="mb-3 flex items-start space-x-3">
                        <Calendar className="mt-1 h-5 w-5 flex-shrink-0 text-[#8b5cf6]" />
                        <div>
                          <p className="text-sm font-medium text-gray-500">Updated At</p>
                          <p className="text-gray-800">{new Date(complaint.updated_at).toLocaleString()}</p>
                        </div>
                      </div>
                      <div className="mb-3 flex items-start space-x-3">
                        <Calendar className="mt-1 h-5 w-5 flex-shrink-0 text-[#8b5cf6]" />
                        <div>
                          <p className="text-sm font-medium text-gray-500">Response Due Date</p>
                          <p className="text-gray-800">
                            {complaint.response_due_date
                              ? new Date(complaint.response_due_date).toLocaleString()
                              : "Not specified"}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start space-x-3">
                        <CheckCircle className="mt-1 h-5 w-5 flex-shrink-0 text-[#8b5cf6]" />
                        <div>
                          <p className="text-sm font-medium text-gray-500">Resolved At</p>
                          <p className="text-gray-800">
                            {complaint.resolved_at
                              ? new Date(complaint.resolved_at).toLocaleString()
                              : "Not resolved yet"}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="mb-2 text-sm font-medium text-gray-500">Feedback</h3>
                    <div className="rounded-lg bg-gray-50 p-4">
                      <div className="mb-3 flex items-start space-x-3">
                        <Star className="mt-1 h-5 w-5 flex-shrink-0 text-[#8b5cf6]" />
                        <div>
                          <p className="text-sm font-medium text-gray-500">Satisfaction Rating</p>
                          <div className="flex items-center">
                            {complaint.satisfaction_rating ? (
                              <div className="flex">
                                {[...Array(5)].map((_, i) => (
                                  <Star
                                    key={i}
                                    className={`h-4 w-4 ${
                                      i < complaint.satisfaction_rating!
                                        ? "fill-yellow-400 text-yellow-400"
                                        : "text-gray-300"
                                    }`}
                                  />
                                ))}
                                <span className="ml-2 text-sm text-gray-600">({complaint.satisfaction_rating}/5)</span>
                              </div>
                            ) : (
                              <span className="text-gray-500">Not rated yet</span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-start space-x-3">
                        <MessageSquare className="mt-1 h-5 w-5 flex-shrink-0 text-[#8b5cf6]" />
                        <div>
                          <p className="text-sm font-medium text-gray-500">Feedback Comments</p>
                          <p className="text-gray-800">{complaint.feedback_comments || "No feedback provided"}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Description and remarks */}
            <div className="mb-6 grid gap-6 md:grid-cols-2">
              <div className="overflow-hidden rounded-xl bg-white shadow-md">
                <div className="border-b border-gray-200 bg-gray-50 p-4">
                  <h3 className="text-lg font-medium text-gray-800">Description</h3>
                </div>
                <div className="p-6">
                  <p className="whitespace-pre-wrap text-gray-700">{complaint.description}</p>
                </div>
              </div>

              <div className="overflow-hidden rounded-xl bg-white shadow-md">
                <div className="border-b border-gray-200 bg-gray-50 p-4 flex justify-between items-center">
                  <h3 className="text-lg font-medium text-gray-800">Remarks</h3>
                  {!isEditingRemarks ? (
                    <button
                      onClick={handleEditRemarks}
                      className="flex items-center text-sm text-[#8b5cf6] hover:text-[#7c3aed] transition-colors"
                    >
                      <Edit className="h-4 w-4 mr-1" />
                      {remarks ? "Edit" : "Add"} Remarks
                    </button>
                  ) : (
                    <div className="flex space-x-2">
                      <button
                        onClick={handleCancelEditRemarks}
                        className="flex items-center text-sm text-gray-500 hover:text-gray-700 transition-colors"
                        disabled={isSavingRemarks}
                      >
                        <X className="h-4 w-4 mr-1" />
                        Cancel
                      </button>
                      <button
                        onClick={handleSaveRemarks}
                        className="flex items-center text-sm text-green-600 hover:text-green-700 transition-colors"
                        disabled={isSavingRemarks}
                      >
                        <Save className="h-4 w-4 mr-1" />
                        {isSavingRemarks ? "Saving..." : "Save"}
                      </button>
                    </div>
                  )}
                </div>
                <div className="p-6">
                  {isEditingRemarks ? (
                    <textarea
                      ref={textareaRef}
                      value={remarks}
                      onChange={(e) => setRemarks(e.target.value)}
                      className="w-full min-h-[120px] p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#8b5cf6] focus:border-transparent"
                      placeholder="Add your remarks here..."
                      disabled={isSavingRemarks}
                    />
                  ) : (
                    <p className="whitespace-pre-wrap text-gray-700">{remarks || "No remarks added"}</p>
                  )}
                </div>
              </div>
            </div>

            {/* SmartOLT quick diagnostics */}
            <div className="mb-6 overflow-hidden rounded-xl bg-white shadow-md border border-slate-200">
              <div className="border-b border-gray-200 bg-gray-50 p-4 flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-800 flex items-center gap-2">
                  <Wifi className="h-4 w-4 text-[#8b5cf6]" />
                  SmartOLT Diagnostics
                </h3>
                <button
                  onClick={async () => {
                    if (!id) return
                    setSmartoltLoading(true)
                    try {
                      const data = await smartoltService.getComplaintDiagnostics(id)
                      setSmartoltDiagnostics(data)
                    } finally {
                      setSmartoltLoading(false)
                    }
                  }}
                  className="text-xs px-3 py-1.5 rounded border border-slate-200 text-slate-700 hover:bg-white"
                >
                  Refresh
                </button>
              </div>
              <div className="p-4 space-y-3">
                {smartoltLoading ? (
                  <p className="text-sm text-slate-500">Loading diagnostics...</p>
                ) : !smartoltDiagnostics?.mapped ? (
                  <p className="text-sm text-amber-700">
                    Customer ONU is not mapped yet. Map from customer profile SmartOLT tab.
                  </p>
                ) : (
                  <>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                      <div className="p-2 rounded border border-slate-100">ONU: {smartoltDiagnostics.onu_external_id}</div>
                      <div className="p-2 rounded border border-slate-100">Status: {smartoltDiagnostics.status || "—"}</div>
                      <div className="p-2 rounded border border-slate-100">Admin: {smartoltDiagnostics.admin_status || "—"}</div>
                      <div className="p-2 rounded border border-slate-100">CATV: {smartoltDiagnostics.catv_status || "—"}</div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
                      <div className="p-2 rounded border border-slate-100">Signal RX/TX: {smartoltDiagnostics.signal_rx ?? "—"} / {smartoltDiagnostics.signal_tx ?? "—"}</div>
                      <div className="p-2 rounded border border-slate-100">Last Sync: {smartoltDiagnostics.fetched_at ? new Date(smartoltDiagnostics.fetched_at).toLocaleString() : "—"}</div>
                    </div>
                    <div className="flex flex-wrap gap-2 pt-1">
                      <button
                        onClick={() => runSmartOLTAction("reboot_onu_by_external_id")}
                        disabled={smartoltActionBusy}
                        className="px-3 h-8 rounded bg-[#8b5cf6] text-white text-xs hover:bg-[#7c3aed] inline-flex items-center gap-1"
                      >
                        <Zap className="h-3.5 w-3.5" /> Reboot ONU
                      </button>
                      <button
                        onClick={() => runSmartOLTAction("resync_onu_config_by_external_id")}
                        disabled={smartoltActionBusy}
                        className="px-3 h-8 rounded border border-slate-200 text-slate-700 text-xs hover:bg-slate-50"
                      >
                        Resync Config
                      </button>
                      <button
                        onClick={() => runSmartOLTAction("disable_onu_by_external_id")}
                        disabled={smartoltActionBusy}
                        className="px-3 h-8 rounded border border-rose-200 text-rose-700 text-xs hover:bg-rose-50"
                      >
                        Disable ONU
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Attachments and resolution proof */}
            <div className="mb-6 grid gap-6 md:grid-cols-2">
              <div className="overflow-hidden rounded-xl bg-white shadow-md">
                <div className="border-b border-gray-200 bg-gray-50 p-4">
                  <h3 className="text-lg font-medium text-gray-800">Attachment</h3>
                </div>
                <div className="p-6">
                  {complaint.attachment_path ? (
                    <div className="flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 p-4">
                      <div className="flex items-center space-x-3">
                        <Paperclip className="h-5 w-5 text-[#8b5cf6]" />
                        <span className="text-gray-700">Complaint Attachment</span>
                      </div>
                      <button
                        onClick={handleDownloadAttachment}
                        className="rounded-md bg-[#8b5cf6] px-3 py-1 text-sm text-white transition duration-300 hover:bg-[#7c3aed]"
                      >
                        Download
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center rounded-lg border border-gray-200 bg-gray-50 p-8">
                      <p className="text-gray-500">No attachment available</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="overflow-hidden rounded-xl bg-white shadow-md">
                <div className="border-b border-gray-200 bg-gray-50 p-4">
                  <h3 className="text-lg font-medium text-gray-800">Resolution Proof</h3>
                </div>
                <div className="p-6">
                  {complaint.resolution_proof ? (
                    <div className="flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 p-4">
                      <div className="flex items-center space-x-3">
                        <FileText className="h-5 w-5 text-[#8b5cf6]" />
                        <span className="text-gray-700">Resolution Document</span>
                      </div>
                      <button
                        onClick={() => {
                          const token = getToken()
                          fetch(`/complaints/resolution-proof/${id}`, {
                            headers: { Authorization: `Bearer ${token}` },
                          })
                            .then((response) => response.blob())
                            .then((blob) => {
                              const url = window.URL.createObjectURL(blob)
                              const a = document.createElement("a")
                              a.style.display = "none"
                              a.href = url
                              a.download = `resolution_proof_${complaint.ticket_number}`
                              document.body.appendChild(a)
                              a.click()
                              window.URL.revokeObjectURL(url)
                            })
                            .catch((error) => console.error("Error:", error))
                        }}
                        className="rounded-md bg-[#8b5cf6] px-3 py-1 text-sm text-white transition duration-300 hover:bg-[#7c3aed]"
                      >
                        Download
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center rounded-lg border border-gray-200 bg-gray-50 p-8">
                      <p className="text-gray-500">No resolution proof available</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Action buttons */}
            <div className="flex flex-wrap justify-end space-x-4">
              <button
                onClick={() => navigate("/complaints")}
                className="rounded-md border border-gray-300 bg-white px-4 py-2 text-gray-700 shadow-sm transition duration-300 hover:bg-gray-50"
              >
                Back to List
              </button>
            </div>
          </div>
        </main>
      </div>
    </div>
  )
}

export default ComplaintDetailPage

